/**
 * Low-level CKEditor wrapper. Prefer {@link MinimalHtmlEditor}, {@link RichHtmlEditor},
 * or {@link ImageHtmlEditor} so toolbar capabilities are obvious at the import site.
 * Import this module directly when you need custom toolbars or plugins.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Editor, EditorConfig } from '@ckeditor/ckeditor5-core';
import type { EventInfo } from '@ckeditor/ckeditor5-utils';
import FormGroupWrap from '../../helpers/FormGroupWrap';
import { loadCkEditorModules } from '@/library/components/form/inputs/html-editor/utils/loadCkEditor';
import { isEditorValueEqual } from '@/library/components/form/inputs/html-editor/utils/normalizeEditorHtml';
import {
  pastedHtmlContainsImages,
  preparePastedEditorContent,
  shouldHandleEditorPaste,
  stripImagesFromPastedHtml
} from '@/library/components/form/inputs/html-editor/utils/preparePastedContent';
import useAutoSaveInputValue from '@/library/utils/hooks/useAutoSaveInputValue';
import type { Attachment } from '@/library/types/files';
import {
  fontBackgroundColor,
  fontColor,
  imageEditingToolbar,
  imagesConfig,
  minimalToolbarConfig,
  pluginConfig,
  tablesConfig,
  toolbarConfig
} from './config';
import {
  attachImageUploadAdapter,
  type ImageUploadAdapterOptions
} from './createImageUploadAdapterPlugin';
import { ckEditorStyles } from './styles';
import { toCssSize } from './toCssSize';

type CkEditorModules = Awaited<ReturnType<typeof loadCkEditorModules>>;

type OrcaEditor = Editor & {
  getData: () => string;
  ui: Editor['ui'] & {
    view: Editor['ui']['view'] & {
      toolbar?: {
        element?: HTMLElement | null;
      } | null;
    };
  };
};

type ClipboardInputData = {
  dataTransfer: {
    getData: (type: string) => string;
    setData: (type: string, value: string) => void;
  };
};

type BasicEditorErrorDetails = {
  willEditorRestart?: boolean;
};

export type BasicEditorProps = {
  label?: ReactNode | (() => ReactNode);
  className?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  onAutoSave?: (value: string) => Promise<unknown>;
  onFocus?: (isFocused: boolean) => void;
  error?: string | null;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  isFocused?: boolean;
  minHeight?: string | number;
  maxHeight?: string | number;
  toolbar?: readonly string[];
  plugins?: readonly string[];
  extraPlugins?: EditorConfig['extraPlugins'];
  extraConfig?: EditorConfig;
  shouldRemovePastedImages?: boolean;
  enableImages?: boolean;
  isFullEditor?: boolean;
  styled?: boolean;
  invisible?: boolean;
  uploadedImagesGroup?: string | null;
  onImageUpload?: (files: Attachment[]) => void;
  uploadImagesRequestPath?: string;
};

const withImagePlugins = (plugins: string[]): string[] => {
  const extra = imagesConfig.plugins.filter(plugin => !plugins.includes(plugin));
  return [...plugins, ...extra];
};

function BasicEditor({
  label,
  value,
  className = '',
  onChange,
  onAutoSave = () => Promise.resolve(null),
  onFocus,
  error,
  disabled = false,
  placeholder = '',
  id,
  isFocused: isFocusedProp,
  minHeight,
  maxHeight,
  toolbar: toolbarProp,
  plugins: pluginsProp,
  extraPlugins,
  extraConfig,
  shouldRemovePastedImages = true,
  enableImages = false,
  isFullEditor = false,
  styled = false,
  invisible = false,
  uploadedImagesGroup = null,
  onImageUpload,
  uploadImagesRequestPath = '/files'
}: BasicEditorProps) {
  const [loadError, setLoadError] = useState<string | null>(null);
  const [internalFocused, setInternalFocused] = useState(false);
  const [editor, setEditor] = useState<OrcaEditor | null>(null);
  const [editorModules, setEditorModules] = useState<CkEditorModules | null>(null);

  const generatedId = useId();
  const editorId = id ?? generatedId;
  const { onAutoSave: autoSaveValue } = useAutoSaveInputValue(onAutoSave);

  const valueRef = useRef(value);
  valueRef.current = value;

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const shouldRemovePastedImagesRef = useRef(shouldRemovePastedImages);
  shouldRemovePastedImagesRef.current = shouldRemovePastedImages;

  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  const isFocused = isFocusedProp ?? internalFocused;
  const displayError = error || loadError;
  const minHeightCss = toCssSize(minHeight);
  const maxHeightCss = toCssSize(maxHeight);
  const hasImageUpload = Boolean(uploadedImagesGroup);

  const imageUploadOptionsRef = useRef<ImageUploadAdapterOptions | null>(null);
  imageUploadOptionsRef.current = uploadedImagesGroup
    ? {
        uploadedImagesGroup,
        uploadImagesRequestPath,
        onImageUpload: onImageUpload
          ? file => {
              onImageUpload([file]);
            }
          : undefined
      }
    : null;

  const resolvedToolbar = useMemo(() => {
    if (!hasImageUpload) return toolbarProp;

    const baseToolbar = toolbarProp ? [...toolbarProp] : [...toolbarConfig];
    if (baseToolbar.includes('imageUpload')) return baseToolbar;

    return [...baseToolbar, ...imagesConfig.toolbar];
  }, [hasImageUpload, toolbarProp]);

  const resolvedExtraConfig = useMemo((): EditorConfig | undefined => {
    if (!hasImageUpload && !extraConfig) return undefined;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { extraPlugins: _extraConfigPlugins, ...restExtraConfig } = extraConfig ?? {};

    return {
      ...(hasImageUpload
        ? {
            image: {
              toolbar: [...imageEditingToolbar]
            }
          }
        : {}),
      ...restExtraConfig
    } as EditorConfig;
  }, [extraConfig, hasImageUpload]);

  const mergedExtraPlugins = useMemo((): EditorConfig['extraPlugins'] => {
    const configExtraPlugins = extraConfig?.extraPlugins;

    if (!configExtraPlugins) return extraPlugins;
    if (!extraPlugins) return configExtraPlugins;

    const basePlugins = Array.isArray(extraPlugins) ? extraPlugins : [extraPlugins];
    const configPlugins = Array.isArray(configExtraPlugins)
      ? configExtraPlugins
      : [configExtraPlugins];

    return [...basePlugins, ...configPlugins] as EditorConfig['extraPlugins'];
  }, [extraConfig?.extraPlugins, extraPlugins]);

  const config = useMemo((): EditorConfig => {
    const basePlugins = [...(pluginsProp ?? pluginConfig)];
    const plugins =
      enableImages || hasImageUpload || !shouldRemovePastedImages
        ? withImagePlugins(basePlugins)
        : basePlugins;

    return {
      placeholder,
      plugins,
      toolbar: [...(resolvedToolbar ?? minimalToolbarConfig)],
      fontColor: { colors: [...fontColor.colors] },
      table: { contentToolbar: [...tablesConfig.contentToolbar] },
      fontBackgroundColor: { colors: [...fontBackgroundColor.colors] },
      link: {
        defaultProtocol: 'https://',
        decorators: {
          isExternal: {
            mode: 'automatic',
            callback: (url: string) => typeof url === 'string' && url.startsWith('https://'),
            attributes: {
              target: '_blank',
              rel: 'noopener noreferrer'
            }
          }
        }
      },
      ...resolvedExtraConfig,
      extraPlugins: mergedExtraPlugins
    } as EditorConfig;
  }, [
    enableImages,
    hasImageUpload,
    mergedExtraPlugins,
    placeholder,
    pluginsProp,
    resolvedExtraConfig,
    resolvedToolbar,
    shouldRemovePastedImages
  ]);

  useEffect(() => {
    let isCancelled = false;

    loadCkEditorModules()
      .then(modules => {
        if (!isCancelled) {
          setEditorModules(modules);
        }
      })
      .catch(moduleLoadError => {
        console.error('Failed to load CKEditor modules', moduleLoadError);

        if (!isCancelled) {
          setLoadError('Failed to load editor');
        }
      });

    return () => {
      isCancelled = true;
      setEditor(null);
    };
  }, []);

  const handleReady = useCallback((innerEditor: Editor) => {
    const orcaEditor = innerEditor as OrcaEditor;
    const editableElement = orcaEditor.ui.getEditableElement();
    const toolbarElement = orcaEditor.ui.view.toolbar?.element;

    if (editableElement?.parentElement && toolbarElement) {
      editableElement.parentElement.insertBefore(toolbarElement, editableElement);
    }

    orcaEditor.ui.focusTracker.on(
      'change:isFocused',
      (_event: EventInfo, _name: string, focused: boolean) => {
        if (disabledRef.current) return;

        setInternalFocused(focused);
        onFocusRef.current?.(focused);
      }
    );

    orcaEditor.editing.view.document.on(
      'clipboardInput',
      (evt: EventInfo, data: ClipboardInputData) => {
        const htmlContent = data.dataTransfer.getData('text/html');
        const plainText = data.dataTransfer.getData('text/plain');

        if (disabledRef.current) return;

        if (
          htmlContent &&
          shouldRemovePastedImagesRef.current &&
          pastedHtmlContainsImages(htmlContent)
        ) {
          data.dataTransfer.setData('text/html', stripImagesFromPastedHtml(htmlContent));
          return;
        }

        if (
          !shouldHandleEditorPaste({
            disabled: disabledRef.current,
            htmlContent,
            plainText,
            shouldRemovePastedImages: shouldRemovePastedImagesRef.current
          })
        ) {
          return;
        }

        const finalContent = preparePastedEditorContent(htmlContent, plainText);
        const viewFragment = orcaEditor.data.processor.toView(finalContent);
        const modelFragment = orcaEditor.data.toModel(viewFragment);

        orcaEditor.model.insertContent(modelFragment, orcaEditor.model.document.selection);
        evt.stop();
      },
      { priority: 'highest' }
    );

    if (imageUploadOptionsRef.current) {
      attachImageUploadAdapter(orcaEditor, () => imageUploadOptionsRef.current);
    }

    setEditor(orcaEditor);
  }, []);

  const handleError = useCallback(
    (_error: Error, details: BasicEditorErrorDetails) => {
      if (details.willEditorRestart && editor) {
        editor.ui.view.toolbar?.element?.remove();
        setEditor(null);
      }
    },
    [editor]
  );

  const handleChange = useCallback(
    (_event: EventInfo, editorInstance: Editor) => {
      if (disabled) return;

      const data = (editorInstance as OrcaEditor).getData();
      if (isEditorValueEqual(data, valueRef.current)) return;

      onChange?.(data);
      autoSaveValue(data);
    },
    [autoSaveValue, disabled, onChange]
  );

  const containerClassName = [
    'ckeditor_container',
    className,
    styled ? 'styled-editor' : '',
    invisible ? 'invisible' : '',
    disabled ? 'is-disabled' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const editorModeClassName = isFullEditor ? 'document-editor' : 'small-editor';
  const toolbarClassName = isFullEditor ? 'document-editor__toolbar' : 'small-editor__toolbar';
  const editableClassName = [
    'ckeditor__editable-container',
    isFullEditor ? 'document-editor__editable-container' : 'small-editor__editable-container',
    !isFullEditor && !isFocused ? 'small-editor__editable-container--notEditMode' : '',
    displayError ? 'has_error' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const editorStyles = ckEditorStyles(
    {
      disabled,
      isFocused,
      isFullEditor,
      styled,
      minHeight: minHeightCss,
      maxHeight: maxHeightCss
    },
    !!displayError
  );

  const CkEditorComponent = editorModules?.CKEditor;
  const DecoupledEditor = editorModules?.DecoupledEditor;

  const editorShell = (
    <div css={editorStyles} className={containerClassName}>
      <div className={editorModeClassName}>
        <div className={toolbarClassName}>
          <div className={editableClassName}>
            {CkEditorComponent && DecoupledEditor ? (
              <CkEditorComponent
                disableWatchdog
                disabled={disabled}
                onReady={handleReady}
                onError={handleError}
                onChange={handleChange}
                id={editorId}
                editor={DecoupledEditor}
                data={value ?? ''}
                config={config}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <FormGroupWrap className={`${className} position-relative`} label={label} error={displayError}>
      {editorShell}
    </FormGroupWrap>
  );
}

export default BasicEditor;
